import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  Link,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@material-ui/core";
import ChatIcon from "@material-ui/icons/Chat";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import DialogAppBar from "../../../Components/DialogAppBar";
import MediaFiles from "../../../Components/MediaFiles";
import NoteSection from "../../../Components/NoteSection";
import Title from "../../../Components/Title";
import EnvSettings from "../../../Utils/EnvSettings";
import {
  ECABIN,
  USER_GROUPS,
  checkAdmin,
  checkRequiredPermissions,
  formatClientInfoString,
  formatTimeWithTZOffset,
  getLCLocation,
  getLCState,
  getLatestLifeCycleState,
  getToken,
  getWarehouses,
  lifeCycleStates as lifeCycleStateOptions,
  readClientDataFromLCS,
  requestErrorHandler,
} from "../../../Utils/Common";
import { showPopupSnackbar } from "../../../redux/actions/snackbarActions";
import DetailTabs from "../../DeviceDetails/DetailTabs";

const server = EnvSettings.server;

const renderStateLabel = (state) => {
  if (!state) {
    return "";
  }
  let label = state.createdAt
    ? formatTimeWithTZOffset(state.createdAt, ECABIN)
    : "";
  if (state.state) {
    label += label ? ` - ${getLCState(state.state)}` : getLCState(state.state);
  }
  if (state.location === "client" && state.clientInfo) {
    const clientInfo = formatClientInfoString(readClientDataFromLCS(state));
    label += clientInfo ? ` - ${clientInfo}` : "";
  } else if (state.location) {
    label += ` - ${getLCLocation(state.location)}`;
  }
  if (state?.Notes?.length) {
    state.Notes.forEach((note) => {
      label += ` - ${note.text}`;
    });
  }
  return label;
};

export default function SubAssemblyDialog({ subAssembly, onClose, onRefresh }) {
  const { t } = useTranslation();
  const subAssemblyLifeCycleStates = useMemo(
    () => subAssembly?.LifeCycleStates || [],
    [subAssembly]
  );
  const deviceLifeCycleStates = useMemo(
    () => subAssembly?.Device?.LifeCycleStates || [],
    [subAssembly]
  );
  const manufacturingEntry = subAssembly?.ManufacturingEntry;
  const manufacturedAt = manufacturingEntry?.createdAt || subAssembly?.createdAt;
  const assignedDeviceLink =
    subAssembly?.Device?.id !== undefined
      ? `${checkAdmin() ? "/cpanel" : ""}/devices/${subAssembly.Device.id}`
      : undefined;
  const handleNotesReload = () => {
    if (typeof onRefresh === "function") {
      onRefresh();
    }
  };

  const lifeCycleContent = (
    <Grid container spacing={3}>
      {deviceLifeCycleStates.length ? (
        <Grid item xs={12} md={4}>
          <Title>
            {t(
              "manufacturing.deviceLifeCycleHistory",
              "Device life cycle history"
            )}
          </Title>

          <Stepper nonLinear activeStep={0} orientation="vertical">
            {deviceLifeCycleStates
              .slice()
              .sort((a, b) => (a.id > b.id ? -1 : 1))
              .map((state, index) => (
                <Step key={state.id || index}>
                  <StepLabel
                    icon={deviceLifeCycleStates.length - index}
                    style={{ position: "relative" }}
                  >
                    {renderStateLabel(state)}
                  </StepLabel>
                </Step>
              ))}
          </Stepper>
        </Grid>
      ) : null}
      <Grid item xs={12} md={4}>
        <Title>
          {t(
            "manufacturing.serialisedSubAssemblyHistory",
            "Sub-assembly life cycle"
          )}
        </Title>
        <Typography variant="body2" color="textSecondary">
          {subAssembly?.Device &&
            t(
              "manufacturing.linkedDevice",
              "This serialised sub-assembly is linked to a device. Life cycle changes are made to the Device object"
            )}
        </Typography>
        {subAssemblyLifeCycleStates.length ? (
          <Stepper nonLinear activeStep={0} orientation="vertical">
            {subAssemblyLifeCycleStates
              .slice()
              .sort((a, b) => (a.id > b.id ? -1 : 1))
              .map((state, index) => (
                <Step key={state.id || index}>
                  <StepLabel
                    icon={subAssemblyLifeCycleStates.length - index}
                    style={{ position: "relative" }}
                  >
                    {renderStateLabel(state)}
                  </StepLabel>
                </Step>
              ))}
          </Stepper>
        ) : (
          <Typography variant="body2" color="textSecondary">
            {t(
              "manufacturing.noLifeCycleStates",
              "No life cycle history available."
            )}
          </Typography>
        )}
        {!subAssembly?.Device ? (
          <Box mt={3}>
            <SubAssemblyLifeCycleEditor
              subAssembly={subAssembly}
              onUpdated={onRefresh}
            />
          </Box>
        ) : null}
      </Grid>
    </Grid>
  );

  const mediaContent = manufacturingEntry?.uuid ? (
    <MediaFiles
      resource={{ type: "manufacturing", number: manufacturingEntry.uuid }}
    />
  ) : (
    <>
      <Title>{t("media", "Media")}</Title>
      <Typography variant="body2" color="textSecondary">
        {t(
          "manufacturing.noManufacturingMedia",
          "Media files are unavailable for this entry."
        )}
      </Typography>
    </>
  );

  const notesContent =
    manufacturingEntry?.id != null ? (
      <NoteSection
        parent={{ ManufacturingEntryId: manufacturingEntry.id }}
        notes={manufacturingEntry?.Notes}
        reload={handleNotesReload}
        noPaper={true}
      />
    ) : (
      <>
        <Title>{t("notes", "Notes")}</Title>
        <Typography variant="body2" color="textSecondary">
          {t(
            "manufacturing.notesUnavailable",
            "Notes are unavailable for this entry."
          )}
        </Typography>
      </>
    );

  return (
    <Dialog open={Boolean(subAssembly)} onClose={onClose} fullScreen>
      {subAssembly ? (
        <DialogContent>
          <DialogAppBar
            header={
              <>
                {subAssembly.number ||
                  t("serialisedSubAssembly.number", "Chassis number")}
                {subAssembly.Device?.serialNumber ? (
                  <Link
                    style={{ color: "white", marginLeft: 8 }}
                    href={assignedDeviceLink}
                  >
                    {`- ${t("devices.serialNumber", "Serial Number")}: ${
                      subAssembly.Device.serialNumber
                    }`}
                  </Link>
                ) : null}
              </>
            }
            onClose={onClose}
          />
          <Grid
            container
            spacing={3}
            style={{
              marginTop: 60,
              marginBottom: 20,
              paddingBottom: 30,
            }}
          >
            <Grid item xs={12}>
              <Paper>
                <Box p={3}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Title>
                        {t(
                          "manufacturing.serialisedSubAssemblyDetails",
                          "Serialised sub-assembly details"
                        )}
                      </Title>
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t("serialisedSubAssembly.number", "Chassis number")}
                        </Typography>
                        <Typography variant="body1">
                          {subAssembly.number ||
                            t("common.notAvailable", "N/A")}
                        </Typography>
                      </Box>
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t("serialisedSubAssembly.model", "Model")}
                        </Typography>
                        <Typography variant="body1">
                          {subAssembly.model || t("common.notAvailable", "N/A")}
                        </Typography>
                      </Box>
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t("serialisedSubAssembly.color", "Color")}
                        </Typography>
                        <Typography variant="body1">
                          {subAssembly.color || t("common.notAvailable", "N/A")}
                        </Typography>
                      </Box>
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t("devices.serialNumber", "Assigned device")}
                        </Typography>
                        {subAssembly.Device ? (
                          <Link href={assignedDeviceLink}>
                            {subAssembly.Device.serialNumber}
                          </Link>
                        ) : (
                          <Typography variant="body1">
                            {t("common.notAvailable", "N/A")}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Title>
                        {t(
                          "manufacturing.manufacturingInfo",
                          "Manufacturing info"
                        )}
                      </Title>
                      {!manufacturingEntry ? (
                        <Typography variant="body2" color="textSecondary">
                          {t(
                            "manufacturing.noManufacturingEntryLinked",
                            "This sub-assembly was added without a manufacturing entry."
                          )}
                        </Typography>
                      ) : null}
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t("stock.item", "Item")}
                        </Typography>
                        <Typography variant="body1">
                          {manufacturingEntry?.StockItem?.name ||
                            t("common.notAvailable", "N/A")}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {manufacturingEntry?.StockItem?.sku}
                        </Typography>
                      </Box>
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t("manufacturing.batchNumber", "Batch number")}
                        </Typography>
                        <Typography variant="body1">
                          {manufacturingEntry?.batchNumber ||
                            t("common.notAvailable", "N/A")}
                        </Typography>
                      </Box>
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t("manufacturing.manufacturedIn", "Manufactured in")}
                        </Typography>
                        <Typography variant="body1">
                          {manufacturingEntry?.StockChanges?.[0]?.Warehouse
                            ?.label || t("common.notAvailable", "N/A")}
                        </Typography>
                      </Box>
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t("manufacturing.manufacturedAt", "Manufactured at")}
                        </Typography>
                        <Typography variant="body1">
                          {manufacturedAt
                            ? formatTimeWithTZOffset(
                                manufacturedAt,
                                ECABIN
                              )
                            : t("common.notAvailable", "N/A")}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <DetailTabs
                activeTabs={[
                  {
                    label: t(
                      "manufacturing.lifeCycleStates",
                      "Life cycle states"
                    ),
                    content: lifeCycleContent,
                  },
                  {
                    label: t("media", "Media"),
                    content: mediaContent,
                  },
                  {
                    label: (
                      <>
                        <ChatIcon
                          style={{
                            fontSize: 20,
                            marginRight: 4,
                            verticalAlign: "middle",
                          }}
                        />
                        {t("notes", "Notes")}
                      </>
                    ),
                    content: notesContent,
                  },
                ]}
              />
            </Grid>
          </Grid>
        </DialogContent>
      ) : null}
    </Dialog>
  );
}

const SubAssemblyLifeCycleEditor = ({ subAssembly, onUpdated }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [warehouses, setWarehouses] = useState([]);
  const [stateValue, setStateValue] = useState("");
  const [locationValue, setLocationValue] = useState("");
  const [qualityValue, setQualityValue] = useState(1);
  const [note, setNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    getWarehouses((items) => setWarehouses(items || []), dispatch);
  }, [dispatch]);

  useEffect(() => {
    const latest = subAssembly?.LifeCycleStates?.length
      ? getLatestLifeCycleState(subAssembly.LifeCycleStates)
      : null;
    setStateValue(latest?.state || "");
    setLocationValue(latest?.location || "");
    setQualityValue(latest?.quality ?? 1);
    setNote("");
  }, [subAssembly]);

  const canEdit = checkRequiredPermissions([
    USER_GROUPS.ADMIN,
    USER_GROUPS.PRODUCTION,
  ]);

  const requiresClientLocation =
    stateValue === "demo" || stateValue === "dispatched";
  const computedLocation =
    requiresClientLocation
      ? "client"
      : stateValue === "missing"
      ? "unknown"
      : locationValue;

  const handleSave = () => {
    if (!canEdit || !subAssembly?.id) {
      return;
    }
    if (!stateValue) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.selectLifeCycleState",
            "Select a life cycle state."
          ),
          severity: "warning",
        })
      );
      return;
    }
    if (!computedLocation) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.selectLifeCycleLocation",
            "Select a location for the life cycle state."
          ),
          severity: "warning",
        })
      );
      return;
    }
    setIsSaving(true);
    fetch(server + "/edit_serialised_subassembly_lcs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        serialisedSubAssemblyId: subAssembly.id,
        lifecyclestate: {
          state: stateValue,
          location: computedLocation,
          quality: qualityValue || null,
        },
        note: note.trim() || undefined,
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (result.error) {
          return;
        }
        dispatch(
          showPopupSnackbar({
            message: t(
              "manufacturing.lifeCycleStateUpdated",
              "Life cycle state updated."
            ),
            severity: "success",
          })
        );
        if (typeof onUpdated === "function") {
          onUpdated();
        }
      })
      .catch(() => {
        dispatch(
          showPopupSnackbar({
            message: t(
              "manufacturing.lifeCycleStateUpdateFailed",
              "Failed to update life cycle state."
            ),
            severity: "error",
          })
        );
      })
      .finally(() => setIsSaving(false));
  };

  const handleReset = () => {
    const latest = subAssembly?.LifeCycleStates?.length
      ? getLatestLifeCycleState(subAssembly.LifeCycleStates)
      : null;
    setStateValue(latest?.state || "");
    setLocationValue(latest?.location || "");
    setQualityValue(latest?.quality ?? 1);
    setNote("");
  };

  if (!canEdit) {
    return null;
  }

  return (
    <Box>
      <Title>
        {t(
          "manufacturing.updateLifeCycleState",
          "Update life cycle state"
        )}
      </Title>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            label={t("deviceList.lcState", "Life cycle state")}
            value={stateValue}
            onChange={(e) => setStateValue(e.target.value)}
            disabled={isSaving}
          >
            {lifeCycleStateOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          {requiresClientLocation ? (
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={t("deviceList.lcLocation", "Location")}
              value={t("manufacturing.clientLocation", "Client")}
              disabled
            />
          ) : stateValue === "missing" ? (
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={t("deviceList.lcLocation", "Location")}
              value={t("manufacturing.unknownLocation", "Unknown")}
              disabled
            />
          ) : (
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              label={t("deviceList.lcLocation", "Location")}
              value={locationValue}
              onChange={(e) => setLocationValue(e.target.value)}
              disabled={isSaving}
            >
              {warehouses.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            label={t("lifeCycleManagement.quality", "Quality")}
            value={qualityValue}
            onChange={(e) => setQualityValue(Number(e.target.value))}
            disabled={isSaving}
          >
            <MenuItem value={1}>1. Class - A</MenuItem>
            <MenuItem value={2}>2. Class - B</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label={t("note", "Note")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            rows={2}
            disabled={isSaving}
          />
        </Grid>
        <Grid item>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? t("common.saving", "Saving…") : t("common.save", "Save")}
          </Button>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={handleReset} disabled={isSaving}>
            {t("cancel", "Cancel")}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};
